import React, { useEffect, useState } from "react";
import {
  Modal,
  Button,
  Tabs,
  Row,
  Col,
  Skeleton,
  Badge,
  Typography,
  Flex,
  notification,
} from "antd";
import { ToolOutlined, UploadOutlined, SendOutlined } from "@ant-design/icons";
import axiosInstance from "utilities/axios";

import RepairModal from "./RepairModal";
import UploadCalibration from "./UploadCalibration";
import DetailsTab from "./DetailsTab";
import HistoryTab from "./HistoryTab";
import CalibrationTab from "./CalibrationTab";
import ShippingPopup from "pages/Equipment/components/Requests/components/RequestDetails/ShippingPopup";
import useRoleCheck from "utilities/hooks/roleCheck";
import UpdateRepairModal from "./UpdateRepairModal";

const { Text } = Typography;

function ViewEquipmentModal({ open, equipment, onClose, updateEquipment }) {
  const { hasPermission } = useRoleCheck();
  const equipmentLock = (!equipment || equipment.status === "Awaiting Shipping" || 
    equipment.status === "Under Repair" || (equipment.requests.find(request => request.requestType === "Repair" &&
      request.currentStatus === "Submitted")));
  const [calibrationModalVisible, setCalibrationModalVisible] = useState(false);
  const [repairModalVisible, setRepairModalVisible] = useState(false);
  const [shipModalVisible, setShipModalVisible] = useState(false);
  const [repairRequest, setRepairRequest] = useState(null);
  const [updateRepairModalVisible, setUpdateRepairModalVisible] =
    useState(false);
  const [completeRepair, setCompleteRepair] = useState(null);

  useEffect(() => {
    if (equipment) {
      setRepairRequest(
        equipment.requests.find(
          (request) =>
            request.currentStatus === "Submitted" &&
            request.requestType === "Repair"
        )
      );

      setCompleteRepair(
        equipment.requests.find(
          (request) =>
            request.currentStatus === "Shipped" &&
            request.requestType === "Repair"
        )
      );
    }
  }, [equipment]);

  const completeRepairRequest = () => {
    axiosInstance
      .post(`/equipment/requests/${completeRepair.id}/complete`)
      .then((response) => {
        notification.success({
          message: "Success",
          description: "Repair request completed successfully!",
        });
        updateEquipment();
        onClose();
      });
  };

  const shipRequestId = equipment?.requests?.find(
    (request) => request.currentStatus === "Approved"
  )?.id;

  const getShippingDestination = () => {
    // get the most recent request for this equipment that has been approved
    const request = equipment?.requests?.find(
      (request) => request.currentStatus === "Approved"
    );
    // if the request is a repair return "Diatec" otherwise return the "Clinic {clinicNo}"
    return request?.requestType === "Repair"
      ? "Diatec"
      : `Clinic ${request?.clinicNo}`;
  };

  return (
    <>
      <Modal
        title="Equipment Details"
        open={open}
        onCancel={onClose}
        width={800} // Increased width
        style={{ maxHeight: "100vh", overflowY: "auto" }}
        footer={[
          <Row justify="space-between" align="middle" style={{ width: "100%" }}>
            <Col>
              {hasPermission(
                ["Audiology"],
                ["Sales.Staff", "Sales.RM", "Sales.Exec"],
                equipment?.clinicNo,
                false
              ) && (
                <>
                  {equipment?.status === "Awaiting Shipping" && (
                    <Flex justify="space-between" align="center">
                      <Text type="secondary" italic style={{ marginRight: 8 }}>
                        This equipment is awaiting shipping to{" "}
                        {getShippingDestination()} and cannot be edited.
                      </Text>
                      <Badge status="warning" dot>
                        <Button
                          key="ship"
                          icon={<SendOutlined />}
                          onClick={() => {
                            setShipModalVisible(true);
                          }}
                        >
                          Set as Shipped
                        </Button>
                      </Badge>
                    </Flex>
                  )}
                  {repairRequest && (
                    <Button
                      key="updateRequestRepair"
                      icon={<ToolOutlined />}
                      style={{ marginRight: 8 }}
                      onClick={() => {
                        setUpdateRepairModalVisible(true);
                      }}
                    >
                      Update Repair Request
                    </Button>
                  )}
                  {!equipmentLock && !repairRequest && !completeRepair && (
                    <>
                      <Button
                        key="requestRepair"
                        icon={<ToolOutlined />}
                        style={{ marginRight: 8 }}
                        onClick={() => {
                          setRepairModalVisible(true);
                        }}
                      >
                        Request Repair
                      </Button>

                      {equipment?.equipment.calibrationAvailable && (
                        <Button
                          key="addCalibration"
                          icon={<UploadOutlined />}
                          onClick={() => setCalibrationModalVisible(true)}
                        >
                          Add Calibration
                        </Button>
                      )}
                    </>
                  )}
                  {completeRepair && (
                    <Button
                      key="completeRepair"
                      icon={<ToolOutlined />}
                      onClick={completeRepairRequest}
                    >
                      Complete Repair
                    </Button>
                  )}
                </>
              )}
            </Col>
          </Row>,
        ]}
      >
        {!equipment ? (
          <Skeleton loading active />
        ) : (
          <>
            <UpdateRepairModal
              open={updateRepairModalVisible}
              onClose={() => setUpdateRepairModalVisible(false)}
              request={repairRequest}
              onUpdate={updateEquipment}
            />
            <RepairModal
              id={equipment.id}
              open={repairModalVisible}
              onClose={() => setRepairModalVisible(false)}
              onUpdate={updateEquipment}
            />
            <UploadCalibration
              id={equipment.id}
              open={calibrationModalVisible}
              onClose={() => setCalibrationModalVisible(false)}
              onUpdate={updateEquipment}
            />
            <Tabs defaultActiveKey="1">
              <Tabs.TabPane tab="Details" key="1">
                <DetailsTab
                  equipment={equipment}
                  equipmentLock={equipmentLock}
                  updateEquipment={updateEquipment}
                  onClose={onClose}
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab="History" key="2">
                <HistoryTab
                  style={{ maxHeight: "70vh", overflowY: "auto" }}
                  requests={equipment.requests}
                />
              </Tabs.TabPane>
              {equipment.equipment.calibrationAvailable && (
                <Tabs.TabPane
                  style={{
                    maxHeight: "70vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                  tab="Calibrations"
                  key="3"
                >
                  <CalibrationTab data={equipment.calibrations} />
                </Tabs.TabPane>
              )}
            </Tabs>
          </>
        )}
      </Modal>
      <ShippingPopup
        itemId={shipRequestId}
        isOpen={shipModalVisible}
        onClose={() => setShipModalVisible(false)}
        onSubmit={() => {
          updateEquipment();
          onClose();
        }}
      />
    </>
  );
}

export default ViewEquipmentModal;
